import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { useSelector, useDispatch } from 'react-redux';
import { store } from './store/store';
import modernTheme from './theme/modernTheme';
import { RootState } from './store/store';
import { getProfile } from './store/slices/authSlice';
import { initializeSafariOptimizations } from './utils/safariOptimizations';
import { CircularProgress, Box } from '@mui/material';

// Universal performance optimizations
import './styles/universal-performance.css';

// Components
import Layout from './components/Layout/Layout';
import ModernLogin from './components/Auth/ModernLogin';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Register from './components/Auth/Register';
import ActivateAccount from './components/Auth/ActivateAccount';
import Dashboard from './components/Dashboard/Dashboard';
import Invoices from './components/Invoices/Invoices';
import Archive from './components/Invoices/Archive';
import SimpleInvoiceDetail from './components/Invoices/SimpleInvoiceDetail';
import ABOFiles from './components/ABO/ABOFiles';
import ABODetail from './components/ABO/ABODetail';
import Users from './components/Users/Users';
import Profile from './components/Profile/Profile';
// import EnhancedISDOCMappingTool from './components/ISDOC/EnhancedISDOCMappingTool'; // Obsolete - using fixed mappings now
import PhotoCaptureContainer from './components/PhotoCapture/PhotoCaptureContainer';
import AIInstructions from './components/AIInstructions/AIInstructions';
import EmailAccounts from './components/Admin/EmailAccounts';
import MyEmails from './components/MyEmails/MyEmails';

// Protected Route Component with token validation
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const { user, token, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (!storedToken) {
        setIsValidating(false);
        return;
      }

      try {
        // Try to get user profile to validate token
        await dispatch(getProfile() as any);
        setIsValidating(false);
      } catch (error) {
        // Token is invalid, remove it and redirect to login
        localStorage.removeItem('token');
        setIsValidating(false);
      }
    };

    validateToken();
  }, [dispatch]);

  if (isValidating) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return isAuthenticated && user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Role Protected Route Component
const RoleProtectedRoute = ({ 
  children, 
  allowedRoles 
}: { 
  children: React.ReactNode;
  allowedRoles: string[];
}) => {
  const dispatch = useDispatch();
  const { user, token, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (!storedToken) {
        setIsValidating(false);
        return;
      }

      try {
        // Try to get user profile to validate token
        await dispatch(getProfile() as any);
        setIsValidating(false);
      } catch (error) {
        // Token is invalid, remove it and redirect to login
        localStorage.removeItem('token');
        setIsValidating(false);
      }
    };

    validateToken();
  }, [dispatch]);

  if (isValidating) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  // Initialize Safari optimizations on app start
  useEffect(() => {
    initializeSafariOptimizations();
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider theme={modernTheme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/login" element={<ModernLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/activate/:token" element={<ActivateAccount />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="archive" element={<Archive />} />
              <Route path="invoices/:id" element={<SimpleInvoiceDetail />} />
              <Route path="invoices/:id/edit" element={<SimpleInvoiceDetail />} />
              <Route path="abo" element={<ABOFiles />} />
              <Route path="abo/:id" element={<ABODetail />} />
              {/* <Route path="isdoc" element={<EnhancedISDOCMappingTool />} /> */}
              {/* ISDOC mapping tool removed - using fixed field mappings now */}
              <Route path="ai-instructions" element={<AIInstructions />} />
              <Route path="photo" element={<PhotoCaptureContainer />} />
              <Route path="my-emails" element={<MyEmails />} />
              <Route 
                path="users" 
                element={
                  <RoleProtectedRoute allowedRoles={['admin', 'superadmin']}>
                    <Users />
                  </RoleProtectedRoute>
                } 
              />
              <Route 
                path="admin/email-accounts" 
                element={
                  <RoleProtectedRoute allowedRoles={['admin', 'superadmin']}>
                    <EmailAccounts />
                  </RoleProtectedRoute>
                } 
              />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Routes>
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App; 